import { createFileRoute, redirect } from "@tanstack/react-router";

import { BrandLockup, HeroSubtitle } from "@/components/marketing/BrandLockup";
import EnhancedAlbumShowcase from "@/components/marketing/enhanced-albums/EnhancedAlbumShowcase";
import EnhancedAlbumValue from "@/components/marketing/enhanced-albums/EnhancedAlbumValue";
import FaqSection from "@/components/marketing/FaqSection";
import HowItWorks from "@/components/marketing/HowItWorks";
import { marketingHead } from "@/components/marketing/marketingHead";
import ProductHero, { ProductShot } from "@/components/marketing/ProductHero";
import { ENHANCED_ALBUMS_LIVE } from "@/components/marketing/products";
import RelatedProducts from "@/components/marketing/RelatedProducts";
import Footer from "@/components/nav/Footer";

const LEDE =
  "Lutely automatically creates alternate versions around your album. Choose the clean, instrumental, acapella, bonus, video, or other exclusive content you want to include, then give fans one richer album experience.";

export const Route = createFileRoute("/enhanced-album")({
  beforeLoad: () => {
    if (!ENHANCED_ALBUMS_LIVE) {
      throw redirect({ to: "/super-single", replace: true });
    }
  },
  component: EnhancedAlbumsPage,
  head: () =>
    marketingHead({
      title:
        "Enhanced Albums | Alternate Versions and Exclusive Content | Lutely",
      description: LEDE,
      path: "/enhanced-album",
    }),
});

const FAQ = [
  {
    question: "What is an Enhanced Album?",
    answer:
      "An Enhanced Album combines a full-length album with alternate versions, bonus tracks, videos, or other exclusive content in one release.",
  },
  {
    question: "Can I enhance an album I already released?",
    answer:
      "Yes. Enhanced Albums can give past releases new life, even when you no longer have every original session file.",
  },
  {
    question: "Do I have to include every alternate version?",
    answer: "No. You choose which versions and extras belong in the release.",
  },
  {
    question: "What can fans do with an Enhanced Album?",
    answer:
      "Fans can stream the release and access the downloads and exclusive content you choose to include.",
  },
];

function EnhancedAlbumsPage() {
  return (
    <main className="flex flex-col bg-white">
      <ProductHero
        title={
          <>
            {/* Wraps on phones, where the name outgrows the column. */}
            <BrandLockup className="max-[680px]:whitespace-normal">
              Enhanced Albums
            </BrandLockup>
            <br />
            <HeroSubtitle>Give every album more to offer.</HeroSubtitle>
          </>
        }
        lede={LEDE}
        cta="Create an Enhanced Album"
        proof={[
          "Alternate versions created automatically",
          "You choose what fans receive",
        ]}
        stage={
          <ProductShot
            src="/marketing/enhanced-albums/enhanced-album-storefront.webp"
            alt="A published Enhanced Album storefront on Lutely"
            imgClassName="!object-contain"
          />
        }
      />
      <HowItWorks
        title="From one album to a complete experience."
        intro="Lutely prepares the available alternate versions automatically. You decide what belongs in the release."
        steps={[
          {
            title: "Choose your album",
            body: "Start with a new album or bring a past release back into your current workflow.",
          },
          {
            title: "Lutely prepares the versions",
            body: "Clean, instrumental, acapella, and other available versions are created automatically. Choose which ones to include.",
          },
          {
            title: "Add extras and publish",
            body: "Add bonus tracks, video, or other exclusive content, set your price, and publish one complete album experience.",
          },
        ]}
      />
      <EnhancedAlbumShowcase />
      <EnhancedAlbumValue />
      <FaqSection title="FAQs" kicker={false} items={FAQ} />
      <RelatedProducts current="enhancedAlbums" />
      <Footer />
    </main>
  );
}
